import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const BANDS = [
  { to: 2500, color: '#1D9E75', label: 'APPROVE' },
  { to: 4000, color: '#E8A020', label: 'CONDITIONS' },
  { to: 6000, color: '#E07020', label: 'REFER' },
  { to: 10000, color: '#E24B4A', label: 'DECLINE' },
];

const DECISION_COLORS = { APPROVE: '#1D9E75', CONDITIONS: '#00B4D8', REFER: '#E8A020', DECLINE: '#E24B4A' };

const polar = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
};

const arcPath = (cx, cy, r, a1, a2) => {
  const [x1, y1] = polar(cx, cy, r, a1);
  const [x2, y2] = polar(cx, cy, r, a2);
  return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
};

function whyReasons(sr) {
  const reasons = [];
  const dims = Object.entries(sr.dimension_scores || {})
    .sort((a, b) => b[1].score - a[1].score)
    .slice(0, 3);
  for (const [, d] of dims) {
    const pct = d.max_points ? Math.round((d.score / d.max_points) * 100) : 0;
    const sev = d.risk_factor > 0.6 ? 'a major driver of' : d.risk_factor > 0.35 ? 'an elevated contributor to' : 'a modest contributor to';
    reasons.push(`${d.name} — ${sev} the score (${d.score} of ${d.max_points} possible points, ${pct}%).`);
  }
  for (const r of (sr.override_rules_triggered || []).slice(0, 2)) {
    reasons.push(`${r.desc} — ${r.action.replace(/_/g, ' ').toLowerCase()} applied.`);
  }
  return reasons.slice(0, 3);
}

export function ScoreGauge({ sr, premium }) {
  const [showWhy, setShowWhy] = useState(false);
  const score = Math.min(Math.max(sr.composite_score || 0, 0), 10000);
  const dc = DECISION_COLORS[sr.decision] || '#5C6B7A';
  const needleAngle = 180 - (score / 10000) * 180;
  const [nx, ny] = polar(110, 100, 72, needleAngle);
  let prev = 0;
  const reasons = whyReasons(sr);

  return (
    <div data-testid="score-gauge" className="flex flex-col items-center">
      <svg width="220" height="118" viewBox="0 0 220 118">
        {BANDS.map((b) => {
          const a1 = 180 - (prev / 10000) * 180;
          const a2 = 180 - (b.to / 10000) * 180;
          prev = b.to;
          return <path key={b.to} d={arcPath(110, 100, 86, a1 - 0.5, a2)} stroke={b.color} strokeWidth="15" fill="none" strokeLinecap="butt" opacity="0.85" />;
        })}
        <line x1="110" y1="100" x2={nx} y2={ny} stroke="#0D1B2A" strokeWidth="3" strokeLinecap="round" />
        <circle cx="110" cy="100" r="5" fill="#0D1B2A" />
        <text x="18" y="115" fontSize="9" fill="#5C6B7A">0</text>
        <text x="184" y="115" fontSize="9" fill="#5C6B7A">10,000</text>
      </svg>
      <p className="text-3xl font-bold font-mono -mt-2" style={{ color: dc }} data-testid="gauge-score">{score.toLocaleString()}</p>
      <span className="mt-2 px-5 py-1.5 rounded-md text-sm font-bold tracking-wide text-white" style={{ background: dc }} data-testid="gauge-decision-badge">
        {sr.decision}
      </span>
      {premium != null && (
        <p className="mt-2 text-2xl font-bold" style={{ color: '#0D1B2A' }} data-testid="gauge-premium">
          ${Number(premium).toLocaleString('en-AU')} <span className="text-xs font-normal" style={{ color: '#5C6B7A' }}>total payable / year</span>
        </p>
      )}
      {sr.decision_seconds != null && (
        <p className="mt-1 text-[11px]" style={{ color: '#5C6B7A' }} data-testid="gauge-time">
          Decision returned in {sr.decision_seconds} seconds from submission
        </p>
      )}
      {reasons.length > 0 && (
        <div className="mt-3 w-full max-w-md">
          <button onClick={() => setShowWhy(!showWhy)} data-testid="why-score-toggle"
            className="w-full flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-md hover:bg-[rgba(26,76,124,0.05)] transition-colors"
            style={{ color: '#1A4C7C' }}>
            Why this score? {showWhy ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {showWhy && (
            <ol className="mt-2 space-y-1.5 text-left" data-testid="why-score-list">
              {reasons.map((r, i) => (
                <li key={i} className="text-xs flex gap-2" style={{ color: '#0D1B2A' }}>
                  <span className="font-bold shrink-0" style={{ color: '#1A4C7C' }}>{i + 1}.</span>{r}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
